import React, { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  ChevronDown,
  Users,
  UserPlus,
  UserCog,
  LayoutDashboard,
  DollarSign,
  FileText,
  Calendar,
  Tag,
  Settings,
  Banknote,
  ClipboardList,
  BarChart3,
  Menu,
  User,
  TrendingUp,
  LogOut,
  Building,
  PieChart,
  CreditCard,
  BarChart,
  ChevronLeft,
} from "lucide-react"
import clsx from "clsx"
import { useAuth } from "@/hooks/useAuth"

interface SidebarProps {
  collapsed: boolean
  onToggleCollapse: () => void
}

interface SidebarItem {
  title: string
  path: string
  icon: React.ReactNode
  roles: string[]
  children?: SidebarItem[]
  badge?: string
}

const CompanyLogo: React.FC = () => {
  const [error, setError] = useState(false)
  return error ? (
    <div className="h-9 w-9 flex items-center justify-center bg-white/10 rounded-lg">
      <Building size={18} className="text-white" />
    </div>
  ) : (
    <img
      src="/LOGO_MOKO_A_fd_vert.png"
      alt="MokoAfrika Logo"
      onError={() => setError(true)}
      className="h-9 w-9 object-contain rounded-lg bg-white p-0.5"
    />
  )
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggleCollapse }) => {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [openMenus, setOpenMenus] = useState<string[]>([])

  const sidebarItems: SidebarItem[] = [
    {
      title: "Dashboard",
      path: "/",
      icon: <LayoutDashboard size={20} />,
      roles: ["admin", "hr_manager", "hr_staff", "employee"],
    },
    {
      title: "Employees",
      path: "/employees",
      icon: <Users size={20} />,
      roles: ["admin", "hr_manager", "hr_staff"],
      children: [
        {
          title: "All Employees",
          path: "/employees",
          icon: <Users size={18} />,
          roles: ["admin", "hr_manager", "hr_staff"],
        },
        {
          title: "Add Employee",
          path: "/employees/add",
          icon: <UserPlus size={18} />,
          roles: ["admin", "hr_manager"],
          badge: "New",
        },
        {
          title: "Profiles",
          path: "/employees/profiles",
          icon: <User size={18} />,
          roles: ["admin", "hr_manager", "hr_staff"],
        },
      ],
    },
    {
      title: "Leave",
      path: "/leaves",
      icon: <Calendar size={20} />,
      roles: ["admin", "hr_manager", "hr_staff", "employee"],
      children: [
        {
          title: "My Leave",
          path: "/leaves",
          icon: <Calendar size={18} />,
          roles: ["employee"],
        },
        {
          title: "All Leaves",
          path: "/leaves/all",
          icon: <ClipboardList size={18} />,
          roles: ["admin", "hr_manager", "hr_staff"],
        },
        {
          title: "Leave Types",
          path: "/leave-types",
          icon: <Tag size={18} />,
          roles: ["admin", "hr_manager"],
        },
        {
          title: "Calendar",
          path: "/leaves/calendar",
          icon: <Calendar size={18} />,
          roles: ["admin", "hr_manager", "hr_staff"],
        },
      ],
    },
    {
      title: "Payroll",
      path: "/payroll",
      icon: <CreditCard size={20} />,
      roles: ["admin", "hr_manager", "hr_staff", "employee"],
      children: [
        {
          title: "Payroll Runs",
          path: "/payroll",
          icon: <DollarSign size={18} />,
          roles: ["admin", "hr_manager", "hr_staff"],
        },
        {
          title: "All Payslips",
          path: "/payroll/all-payslips",
          icon: <FileText size={18} />,
          roles: ["admin", "hr_manager", "hr_staff"],
        },
        {
          title: "My Payslips",
          path: "/my-payslips",
          icon: <FileText size={18} />,
          roles: ["employee"],
        },
        {
          title: "History",
          path: "/payroll/history",
          icon: <BarChart size={18} />,
          roles: ["admin", "hr_manager", "hr_staff"],
        },
      ],
    },
    {
      title: "Loans",
      path: "/loans",
      icon: <Banknote size={20} />,
      roles: ["admin", "hr_manager", "hr_staff", "employee"],
      children: [
        {
          title: "My Loans",
          path: "/loans",
          icon: <Banknote size={18} />,
          roles: ["employee"],
        },
        {
          title: "All Loans",
          path: "/loans/all",
          icon: <ClipboardList size={18} />,
          roles: ["admin", "hr_manager", "hr_staff"],
        },
        {
          title: "Applications",
          path: "/loans/applications",
          icon: <FileText size={18} />,
          roles: ["admin", "hr_manager", "hr_staff"],
        },
        {
          title: "Loan Types",
          path: "/loans/types",
          icon: <Settings size={18} />,
          roles: ["admin", "hr_manager"],
        },
      ],
    },
    {
      title: "Reports",
      path: "/reports",
      icon: <PieChart size={20} />,
      roles: ["admin", "hr_manager"],
      children: [
        {
          title: "HR Analytics",
          path: "/reports/analytics",
          icon: <BarChart3 size={18} />,
          roles: ["admin", "hr_manager"],
        },
        {
          title: "Payroll Reports",
          path: "/reports/payroll",
          icon: <DollarSign size={18} />,
          roles: ["admin", "hr_manager"],
        },
        {
          title: "Leave Reports",
          path: "/reports/leaves",
          icon: <Calendar size={18} />,
          roles: ["admin", "hr_manager"],
        },
        {
          title: "Employee Reports",
          path: "/reports/employees",
          icon: <Users size={18} />,
          roles: ["admin", "hr_manager"],
        },
      ],
    },
    {
      title: "Exchange Rates",
      path: "/exchange-rates",
      icon: <TrendingUp size={20} />,
      roles: ["admin", "hr_manager", "hr_staff", "employee"],
    },
    {
      title: "Administration",
      path: "/admin",
      icon: <Settings size={20} />,
      roles: ["admin"],
      children: [
        {
          title: "Settings",
          path: "/admin/settings",
          icon: <Settings size={18} />,
          roles: ["admin"],
        },
        {
          title: "User Management",
          path: "/admin/credentials",
          icon: <UserCog size={18} />,
          roles: ["admin"],
        },
        {
          title: "System Logs",
          path: "/admin/logs",
          icon: <ClipboardList size={18} />,
          roles: ["admin"],
        },
      ],
    },
  ]

  // Auto-open menu based on current route
  useEffect(() => {
    const currentPath = location.pathname

    const findParentPaths = (items: SidebarItem[], targetPath: string): string[] => {
      const parents: string[] = []
      const findParent = (items: SidebarItem[], targetPath: string): boolean => {
        for (const item of items) {
          if (item.path === targetPath) return true
          if (item.children) {
            const isChildActive = findParent(item.children, targetPath)
            if (isChildActive) {
              parents.push(item.path)
              return true
            }
          }
        }
        return false
      }
      findParent(items, targetPath)
      return parents
    }

    const parentPaths = findParentPaths(sidebarItems, currentPath)
    setOpenMenus(parentPaths)
  }, [location.pathname])

  const toggleSubMenu = (path: string) => {
    setOpenMenus((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    )
  }

  const handleItemClick = (item: SidebarItem) => {
    if (item.children && item.children.length > 0) {
      toggleSubMenu(item.path)
    } else {
      navigate(item.path)
    }
  }

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/"
    const item = sidebarItems.find((i) => i.path === path)
    if (item?.children) {
      return item.children.some(
        (child) =>
          location.pathname === child.path ||
          location.pathname.startsWith(child.path + "/")
      )
    }
    return location.pathname === path || location.pathname.startsWith(path + "/")
  }

  const isChildActiveCheck = (item: SidebarItem) => {
    if (!item.children) return false
    return item.children.some((child) => isActive(child.path))
  }

  const hasAccess = (roles: string[]) => roles.includes(user?.role || "")

  const renderSidebarItem = (item: SidebarItem, level = 0) => {
    if (!hasAccess(item.roles)) return null

    const isOpen = openMenus.includes(item.path)
    const hasChildren = item.children && item.children.length > 0
    const isItemActive = isActive(item.path)
    const isParentActive = hasChildren && isChildActiveCheck(item)
    const active = isItemActive || isParentActive

    return (
      <div key={item.path}>
        <button
          onClick={() => handleItemClick(item)}
          className={clsx(
            "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 group relative",
            level > 0 && "ml-3 pl-3",
            active
              ? "bg-white/10 text-white"
              : "text-indigo-200 hover:bg-white/[0.06] hover:text-white"
          )}
        >
          {active && level === 0 && (
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-brand-500 rounded-r-full" />
          )}
          <span
            className={clsx(
              "flex-shrink-0 transition-colors",
              active ? "text-brand-500" : "text-indigo-400 group-hover:text-indigo-200"
            )}
          >
            {item.icon}
          </span>

          {!collapsed && (
            <>
              <span className="flex-1 text-left truncate">{item.title}</span>
              {item.badge && (
                <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-brand-500 text-white">
                  {item.badge}
                </span>
              )}
              {hasChildren && (
                <ChevronDown
                  size={14}
                  className={clsx(
                    "transition-transform duration-200 text-indigo-400",
                    isOpen ? "rotate-0" : "-rotate-90"
                  )}
                />
              )}
            </>
          )}
        </button>

        {hasChildren && isOpen && !collapsed && (
          <div className="mt-0.5 space-y-0.5 border-l border-indigo-700/40 ml-[22px]">
            {item.children!.map((child) => renderSidebarItem(child, level + 1))}
          </div>
        )}
      </div>
    )
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate("/login")
    } catch (error) {
      console.error("Logout failed:", error)
    }
  }

  return (
    <aside
      className={clsx(
        "fixed top-0 left-0 h-screen flex flex-col transition-all duration-300 z-50",
        "bg-gradient-to-b from-indigo-950 via-indigo-900 to-indigo-950",
        "shadow-sidebar",
        collapsed ? "w-[68px]" : "w-64"
      )}
    >
      {/* Logo Header */}
      <div
        className={clsx(
          "flex items-center h-16 px-4 border-b border-white/5",
          collapsed ? "justify-center" : "justify-between"
        )}
      >
        {!collapsed ? (
          <div className="flex items-center gap-3">
            <CompanyLogo />
            <div>
              <h1 className="text-sm font-bold text-white tracking-wide">
                MokoAfrika
              </h1>
              <p className="text-[10px] text-indigo-300 font-medium">
                HR Platform
              </p>
            </div>
          </div>
        ) : (
          <CompanyLogo />
        )}

        {!collapsed && (
          <button
            onClick={onToggleCollapse}
            className="p-1.5 rounded-md hover:bg-white/10 transition-colors"
            title="Collapse sidebar"
          >
            <ChevronLeft size={16} className="text-indigo-300" />
          </button>
        )}
      </div>

      {/* Collapsed toggle */}
      {collapsed && (
        <div className="flex justify-center py-2 border-b border-white/5">
          <button
            onClick={onToggleCollapse}
            className="p-1.5 rounded-md hover:bg-white/10 transition-colors"
            title="Expand sidebar"
          >
            <Menu size={16} className="text-indigo-300" />
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2.5 space-y-0.5 sidebar-scroll">
        {!collapsed && (
          <div className="px-3 mb-3">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-indigo-500">
              Main Menu
            </p>
          </div>
        )}
        {sidebarItems.map((item) => renderSidebarItem(item))}
      </nav>

      {/* User Footer */}
      <div className="border-t border-white/5 p-3">
        {!collapsed ? (
          <div className="space-y-2">
            <button
              onClick={() => navigate("/profile")}
              className="w-full flex items-center gap-3 p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            >
              <div className="w-8 h-8 bg-brand-500 rounded-lg flex items-center justify-center text-white text-xs font-bold">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-[13px] font-medium text-white truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-[11px] text-indigo-300 capitalize truncate">
                  {user?.role?.replace("_", " ")}
                </p>
              </div>
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-indigo-300 hover:text-red-300 hover:bg-red-500/10 transition-colors text-[13px] font-medium"
            >
              <LogOut size={15} />
              <span>Sign out</span>
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <button
              onClick={() => navigate("/profile")}
              className="w-8 h-8 bg-brand-500 rounded-lg flex items-center justify-center text-white text-xs font-bold"
            >
              {user?.firstName?.[0]}
            </button>
            <button
              onClick={handleLogout}
              className="p-1.5 text-indigo-300 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
              title="Sign out"
            >
              <LogOut size={14} />
            </button>
          </div>
        )}
      </div>

      <style>{`
        .sidebar-scroll::-webkit-scrollbar { width: 3px; }
        .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
        .sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 3px; }
        .sidebar-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.15); }
      `}</style>
    </aside>
  )
}

export default Sidebar
